import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ErrorMsg, Input, Text } from '@fragments';
import { IconPlus, IconTrash, IconAt } from '@tabler/icons-react';

// A field of the editor. `Input` itself is controlled, so a change from outside (a template, the
// @ button) shows at once; the limit is the contract's.
export function Field({ label, value, onChange, max, error, multiline, rows = 3, placeholder, helper, ariaLabel, counter = true, ...props }) {
  return <div className="studio-automation-field">
    <Input fullWidth label={label} value={value} placeholder={placeholder} helper={helper} error={error || undefined} size={counter ? max : undefined} multiline={multiline} rows={multiline ? rows : undefined} aria-label={ariaLabel || label}
      onInput={next => onChange(max ? next.slice(0, max) : next)} {...props} />
    {error && <ErrorMsg>{error}</ErrorMsg>}
  </div>;
}

// A message that can call the person by their @.
export function MessageField({ value, onChange, max, error, label, rows = 4, placeholder }) {
  const { t } = useTranslation('studioAutomations');
  return <div className="studio-automation-message">
    <Field label={label} value={value} onChange={onChange} max={max} error={error} multiline rows={rows} placeholder={placeholder} emoji />
    <Button className="studio-automation-message__at" size="micro" variant="text" color="overlay" startIcon={<IconAt />} preventLoading onClick={() => onChange(`${value}{{username}}`.slice(0, max))}>{t('insertUsername')}</Button>
  </div>;
}

// One choice of a radio group; its detail opens below while it is the chosen one.
export function OptionCard({ selected, onSelect, title, hint, mark, children }) {
  return <div className={`studio-automation-option${selected ? ' is-chosen' : ''}`}>
    <div className="studio-automation-option__row">
      <button type="button" className="studio-automation-option__head" role="radio" aria-checked={selected} onClick={onSelect}>
        <span className="studio-automation-option__dot" aria-hidden="true" />
        <span className="studio-automation-option__title">
          <Text.label contrast={selected ? 9 : 8}>{title}</Text.label>
          {hint && <Text.caption contrast={6}>{hint}</Text.caption>}
        </span>
      </button>
      {mark}
    </div>
    {selected && children && <div className="studio-automation-option__body">{children}</div>}
  </div>;
}

// An optional part of the sequence: a switch, and what it asks while on.
export function ToggleCard({ on, onChange, title, hint, mark, children, disabled }) {
  return <div className={`studio-automation-option is-toggle${on ? ' is-chosen' : ''}`}>
    <div className="studio-automation-option__head">
      <span className="studio-automation-option__title">
        <Text.label contrast={on ? 9 : 8}>{title}</Text.label>
        {hint && <Text.caption contrast={6}>{hint}</Text.caption>}
      </span>
      {mark}
      <Input.Switch value={on} onInput={onChange} disabled={disabled} size="small" aria-label={title} />
    </div>
    {on && children && <div className="studio-automation-option__body">{children}</div>}
  </div>;
}

// The link buttons of a message: up to 3, each a label and an https address.
export function LinkRows({ buttons, onChange, limit, path, errors }) {
  const { t } = useTranslation('studioAutomations');
  const set = (index, field, value) => onChange(buttons.map((button, position) => (position === index ? { ...button, [field]: value } : button)));
  const normalize = (index, value) => { const text = value.trim(); if (text && !/^[a-z]+:\/\//i.test(text)) set(index, 'url', `https://${text}`); };
  return <div className="studio-automation-links">
    {buttons.map((button, index) => <div key={index} className="studio-automation-links__row">
      <Field label={t('linkTitle')} value={button.title} onChange={value => set(index, 'title', value)} max={limit.title} counter={false} placeholder={t('linkTitlePlaceholder')} error={errors[`${path}.buttons.${index}.title`]?.[0]} />
      <Field label={t('linkUrl')} value={button.url} onChange={value => set(index, 'url', value)} placeholder="https://" inputMode="url" onBlur={event => normalize(index, event.target.value)} error={errors[`${path}.buttons.${index}.url`]?.[0]} />
      <Button className="studio-automation-links__remove" variant="text" color="overlay" size="small" icon={<IconTrash />} aria-label={t('linkRemove')} tooltip={t('linkRemove')} preventLoading onClick={() => onChange(buttons.filter((_, position) => position !== index))} />
    </div>)}
    {buttons.length < limit.count && <Button variant="dashed" color="overlay" fullWidth startIcon={<IconPlus />} preventLoading onClick={() => onChange([...buttons, { title: '', url: '' }])}>{t('linkAdd')}</Button>}
  </div>;
}
